// HRMS — Org Structure (Departments, Designations, Grades, Cost Centers, Shifts)
import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import hr from '../api';
import { PageHeader, Btn, Drawer, Input, Select, Table, Pill, SOFT, PANEL } from '../ui';

const TABS = [
  { k: 'departments', l: 'Departments' },
  { k: 'designations', l: 'Designations' },
  { k: 'grades', l: 'Grades' },
  { k: 'cost-centers', l: 'Cost Centers' },
  { k: 'shifts', l: 'Shifts' },
];

export default function Org({ companyId }) {
  const [tab, setTab] = useState('departments');
  return (
    <div data-testid="hr-org">
      <PageHeader title="Org Structure" subtitle="Departments · Designations · Grades · Cost Centers · Shifts" />
      <div className="flex gap-2 mb-4">
        {TABS.map((t) => (
          <button key={t.k} onClick={() => setTab(t.k)}
            className={`px-3 py-1.5 rounded text-xs tracking-[0.15em] uppercase ${tab === t.k ? 'bg-white text-neutral-900' : 'text-white/55 hover:text-white border border-white/10'}`}>
            {t.l}
          </button>
        ))}
      </div>
      {tab === 'departments' && <SimpleCrud companyId={companyId} path="/departments" title="Department"
        fields={[
          { k: 'name', l: 'Name *', required: true },
          { k: 'code', l: 'Code' },
          { k: 'description', l: 'Description' },
        ]}
        columns={[
          { key: 'name', label: 'Name', render: (r) => <span className="font-medium text-white">{r.name}</span> },
          { key: 'code', label: 'Code', render: (r) => <span className="font-mono text-xs text-white/55">{r.code}</span> },
          { key: 'headcount', label: 'Headcount', align: 'right', render: (r) => <span className="text-emerald-300">{r.headcount}</span> },
        ]} />}
      {tab === 'designations' && <SimpleCrud companyId={companyId} path="/designations" title="Designation"
        fields={[
          { k: 'name', l: 'Name *', required: true },
          { k: 'code', l: 'Code' },
          { k: 'grade', l: 'Grade' },
        ]}
        columns={[
          { key: 'name', label: 'Name', render: (r) => <span className="font-medium text-white">{r.name}</span> },
          { key: 'grade', label: 'Grade' },
        ]} />}
      {tab === 'grades' && <SimpleCrud companyId={companyId} path="/grades" title="Grade"
        fields={[
          { k: 'name', l: 'Name *', required: true },
          { k: 'level', l: 'Level', type: 'number' },
          { k: 'min_ctc', l: 'Min CTC', type: 'number' },
          { k: 'max_ctc', l: 'Max CTC', type: 'number' },
        ]}
        columns={[
          { key: 'name', label: 'Name' },
          { key: 'level', label: 'Level' },
          { key: 'min_ctc', label: 'Min CTC', align: 'right' },
          { key: 'max_ctc', label: 'Max CTC', align: 'right' },
        ]} />}
      {tab === 'cost-centers' && <SimpleCrud companyId={companyId} path="/cost-centers" title="Cost Center"
        fields={[
          { k: 'name', l: 'Name *', required: true },
          { k: 'code', l: 'Code' },
        ]}
        columns={[{ key: 'name', label: 'Name' }, { key: 'code', label: 'Code' }]} />}
      {tab === 'shifts' && <SimpleCrud companyId={companyId} path="/shifts" title="Shift"
        fields={[
          { k: 'name', l: 'Name *', required: true },
          { k: 'start_time', l: 'Start (HH:MM) *', required: true },
          { k: 'end_time', l: 'End (HH:MM) *', required: true },
          { k: 'break_minutes', l: 'Break (min)', type: 'number' },
          { k: 'grace_minutes_late', l: 'Grace Late (min)', type: 'number' },
        ]}
        columns={[
          { key: 'name', label: 'Name' },
          { key: 'start_time', label: 'Start' },
          { key: 'end_time', label: 'End' },
          { key: 'break_minutes', label: 'Break', align: 'right' },
        ]} />}
    </div>
  );
}

export function SimpleCrud({ companyId, path, title, fields, columns, defaults = {} }) {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ ...defaults });
  const reload = async () => setRows(await hr.get(path, { company_id: companyId }));
  useEffect(() => { if (companyId) reload(); /* eslint-disable-next-line */ }, [companyId]);
  const save = async () => {
    try {
      const required = fields.filter(f => f.required && !form[f.k]);
      if (required.length) return toast.error(`${required[0].l} required`);
      const payload = { ...form, company_id: companyId };
      fields.forEach(f => { if (f.type === 'number' && payload[f.k] != null) payload[f.k] = Number(payload[f.k]); });
      await hr.post(path, payload);
      toast.success('Saved'); setOpen(false); setForm({ ...defaults }); reload();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
  };
  const del = async (id) => {
    if (!window.confirm('Delete?')) return;
    try { await hr.del(`${path}/${id}`); toast.success('Deleted'); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); }
  };
  return (
    <div>
      <div className="flex justify-end mb-3">
        <Btn variant="accent" onClick={() => { setForm({ ...defaults }); setOpen(true); }}>+ New {title}</Btn>
      </div>
      <Table columns={[
        ...columns,
        { key: 'a', label: '', render: (r) => <button className="text-xs text-rose-400/70 hover:text-rose-400" onClick={() => del(r.id)}>Delete</button> }
      ]} rows={rows} empty={`No ${title.toLowerCase()}s yet.`} />
      <Drawer open={open} onClose={() => setOpen(false)} title={`New ${title}`} width="max-w-lg">
        <div className="space-y-3">
          {fields.map((f) => (
            <Input key={f.k} label={f.l} type={f.type || 'text'} value={form[f.k] ?? ''}
              onChange={(e) => setForm({ ...form, [f.k]: e.target.value })} />
          ))}
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <Btn variant="ghost" onClick={() => setOpen(false)}>Cancel</Btn>
          <Btn variant="accent" onClick={save}>Save</Btn>
        </div>
      </Drawer>
    </div>
  );
}
